import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Row, Spinner } from "reactstrap";
import apiGd from "services/apiGd";

const token = () => localStorage.getItem("@QRExpress:tokenGd");

const reqConfig = () => ({
  headers: { Authorization: `Bearer ${token()}` },
});

function InfoRow({ label, value }) {
  if (value === null || value === undefined) return null;
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "8px 0",
        borderBottom: "1px solid #f0f0f0",
      }}
    >
      <span style={{ color: "#6c757d", fontSize: 13, fontWeight: 500 }}>
        {label}
      </span>
      <span style={{ color: "#333", fontSize: 13, fontWeight: 600 }}>
        {String(value)}
      </span>
    </div>
  );
}

function BoolBadge({ value }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 12,
        fontSize: 12,
        fontWeight: 600,
        backgroundColor: value ? "#d4edda" : "#f8d7da",
        color: value ? "#155724" : "#721c24",
      }}
    >
      {value ? "Sim" : "Não"}
    </span>
  );
}

function SectionCard({ title, children, color = "#007bff" }) {
  return (
    <Card style={{ marginBottom: 16, border: "none", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
      <CardBody style={{ padding: "16px 20px" }}>
        <h6
          style={{
            color,
            fontWeight: 700,
            marginBottom: 12,
            paddingBottom: 8,
            borderBottom: `2px solid ${color}22`,
            fontSize: 13,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          {title}
        </h6>
        {children}
      </CardBody>
    </Card>
  );
}

export default function PrinterControl({ notify, restaurant_id }) {
  const [printers, setPrinters] = useState([]);
  const [selectedPrinter, setSelectedPrinter] = useState(null);
  const [loadingPrinters, setLoadingPrinters] = useState(false);
  const [loadingTest, setLoadingTest] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const fetchPrinters = useCallback(async () => {
    setLoadingPrinters(true);
    try {
      const res = await apiGd.get(
        `/admin/restaurants/${restaurant_id}/printers`,
        reqConfig()
      );
      setPrinters(res.data);
      if (res.data.length > 0) {
        setSelectedPrinter(res.data[0]);
      }
    } catch (err) {
      notify("tr", "danger", "Erro ao buscar impressoras");
    } finally {
      setLoadingPrinters(false);
    }
  }, [restaurant_id]);

  useEffect(() => {
    fetchPrinters();
  }, [fetchPrinters]);

  const handleSelectPrinter = (e) => {
    const found = printers.find((p) => String(p.id) === e.target.value);
    setSelectedPrinter(found || null);
    setTestResult(null);
  };

  const handleTestPrint = useCallback(async () => {
    if (!selectedPrinter) return;
    setLoadingTest(true);
    setTestResult(null);
    try {
      const res = await apiGd.post(
        `/admin/restaurants/${restaurant_id}/printers/${selectedPrinter.id}/test-print`,
        {},
        reqConfig()
      );
      setTestResult(res.data);
      notify("tr", "success", "Impressão de teste enviada!");
    } catch (err) {
      notify("tr", "danger", err?.response?.data?.message || "Erro ao enviar impressão de teste");
    } finally {
      setLoadingTest(false);
    }
  }, [selectedPrinter, restaurant_id]);

  if (loadingPrinters) {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <Spinner color="primary" />
        <p style={{ marginTop: 12, color: "#6c757d" }}>Carregando impressoras...</p>
      </div>
    );
  }

  if (!loadingPrinters && printers.length === 0) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: 40,
          color: "#6c757d",
          background: "#f8f9fa",
          borderRadius: 8,
        }}
      >
        <i className="nc-icon nc-paper" style={{ fontSize: 40, marginBottom: 12, display: "block" }} />
        <p>Nenhuma impressora cadastrada para este restaurante.</p>
      </div>
    );
  }

  return (
    <div>
      <Card style={{ marginBottom: 20, border: "none", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
        <CardBody style={{ padding: "20px 24px" }}>
          <Row style={{ alignItems: "center" }}>
            <Col md="5">
              <label style={{ fontWeight: 600, fontSize: 13, color: "#333", marginBottom: 6, display: "block" }}>
                Selecionar Impressora
              </label>
              <select
                value={selectedPrinter?.id || ""}
                onChange={handleSelectPrinter}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 6,
                  border: "1px solid #ced4da",
                  fontSize: 14,
                  color: "#333",
                  backgroundColor: "#fff",
                  outline: "none",
                }}
              >
                {printers.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} {p.ip ? `— ${p.ip}` : p.host ? `— ${p.host}` : ""}
                  </option>
                ))}
              </select>
            </Col>
            <Col md="3" style={{ paddingTop: 22 }}>
              <Button
                onClick={handleTestPrint}
                disabled={loadingTest || !selectedPrinter}
                style={{
                  backgroundColor: "#2ec9b7",
                  border: "none",
                  borderRadius: 6,
                  padding: "10px 20px",
                  fontWeight: 600,
                  fontSize: 14,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                {loadingTest ? <Spinner size="sm" /> : <i className="nc-icon nc-paper" />}
                Impressão de Teste
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {selectedPrinter && (
        <Row>
          <Col md="6">
            <SectionCard title="Configurações da Impressora" color="#007bff">
              <InfoRow label="ID" value={selectedPrinter.id} />
              <InfoRow label="Nome" value={selectedPrinter.name} />
              <InfoRow label="IP" value={selectedPrinter.ip} />
              <InfoRow label="Host" value={selectedPrinter.host} />
              <InfoRow label="Porta" value={selectedPrinter.port} />
              <InfoRow label="Driver" value={selectedPrinter.driver} />
              <InfoRow label="Modelo" value={selectedPrinter.model} />
              <InfoRow label="Conexão" value={selectedPrinter.connection_method} />
              <InfoRow label="Largura" value={selectedPrinter.width} />
              <InfoRow label="Tamanho" value={selectedPrinter.tamanho} />
            </SectionCard>
          </Col>
          <Col md="6">
            <SectionCard title="Permissões de Impressão" color="#6f42c1">
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {[
                  { label: "Imprimir Pedidos", key: "print_orders_allowed" },
                  { label: "Imprimir Comandas", key: "print_bills_allowed" },
                  { label: "Imprimir Relatórios", key: "print_reports_allowed" },
                  { label: "Imprimir NFC-e no App", key: "print_nfce_allowed" },
                  { label: "Imprimir Autoatendimento", key: "print_self_service_allowed" },
                  { label: "Limitado por Categoria", key: "is_category_limited" },
                  { label: "Usar por Produto", key: "use_products" },
                  { label: "Imprimir Nome da Categoria", key: "print_category_name" },
                ].map(({ label, key }) => (
                  <div
                    key={key}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "6px 0",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <span style={{ color: "#6c757d", fontSize: 13, fontWeight: 500 }}>{label}</span>
                    <BoolBadge value={selectedPrinter[key]} />
                  </div>
                ))}
              </div>
            </SectionCard>
          </Col>
        </Row>
      )}

      {testResult && (
        <div>
          <h5 style={{ fontWeight: 700, color: "#333", marginBottom: 16 }}>
            Resultado da Impressão de Teste
          </h5>
          <Row>
            <Col md="6">
              <SectionCard title="Restaurante" color="#28a745">
                <InfoRow label="ID" value={testResult.data?.restaurant?.id} />
                <InfoRow label="Nome" value={testResult.data?.restaurant?.name} />
                <InfoRow label="Nome Fantasia" value={testResult.data?.restaurant?.fantasy_name} />
                <InfoRow label="Telefone" value={testResult.data?.restaurant?.phone} />
                <InfoRow label="CNPJ" value={testResult.data?.restaurant?.cnpj} />
                {[
                  { label: "Auto Imprimir Pedidos", key: "auto_print_orders" },
                  { label: "Auto Imprimir Delivery", key: "auto_print_delivery" },
                ].map(({ label, key }) => (
                  <div key={key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #f0f0f0" }}>
                    <span style={{ color: "#6c757d", fontSize: 13, fontWeight: 500 }}>{label}</span>
                    <BoolBadge value={testResult.data?.restaurant?.[key]} />
                  </div>
                ))}
              </SectionCard>
            </Col>
            <Col md="6">
              <SectionCard title="Configurações do Restaurante" color="#fd7e14">
                {testResult.data?.restaurant_settings ? (
                  <>
                    <InfoRow label="Plano" value={testResult.data.restaurant_settings.plan} />
                    {[
                      { label: "Novo App de Impressão", key: "using_new_printer" },
                      { label: "Imprimir NFC-e Dashboard", key: "print_nfce_dashboard" },
                      { label: "Título Categoria Complemento", key: "print_complement_category_title" },
                    ].map(({ label, key }) => (
                      <div key={key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #f0f0f0" }}>
                        <span style={{ color: "#6c757d", fontSize: 13, fontWeight: 500 }}>{label}</span>
                        <BoolBadge value={testResult.data.restaurant_settings[key]} />
                      </div>
                    ))}
                  </>
                ) : (
                  <p style={{ color: "#6c757d", fontSize: 13 }}>Sem configurações disponíveis.</p>
                )}
              </SectionCard>
            </Col>
          </Row>
          <Row>
            <Col md="12">
              <SectionCard title="Detalhes do Envio" color="#6c757d">
                <Row>
                  <Col md="4">
                    <InfoRow label="Disparado em" value={testResult.data?.test_print?.triggered_at ? new Date(testResult.data.test_print.triggered_at).toLocaleString("pt-BR") : "-"} />
                  </Col>
                  <Col md="4">
                    <InfoRow label="PrintableItem ID" value={testResult.printable_item_id} />
                  </Col>
                  <Col md="4">
                    <InfoRow label="PrintableObject ID" value={testResult.printable_object_id} />
                  </Col>
                </Row>
              </SectionCard>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
}
